import { useCallback, useRef, useState } from 'react'
import type { TerminalSocket } from '../transport/ws'

/**
 * The composer doubles as a direction pad on narrow screens (docs/UI-COMPONENTS.md §5.2).
 *
 * Swipes send arrow keys; a tap still focuses the textarea to type. Ported behaviour,
 * with the two details that are easy to get wrong:
 *
 *  - `pointerdown` MUST preventDefault. Otherwise the touch focuses the textarea, which
 *    pops the soft keyboard — and retracts it again when the swipe blurs. The original's
 *    comment is explicit about this.
 *  - Past the threshold we capture the pointer and keep preventing default, which stops
 *    the browser turning the drag into caret placement or text selection.
 *
 * CSS must also set `touch-action: none` on the composer at this breakpoint, or the
 * browser claims the gesture as a scroll before we see it.
 *
 * Improved over the original: axis choice uses hysteresis rather than a bare
 * `abs(dx) > abs(dy)`, which jitters near 45°. The dominant axis must lead by 1.3x,
 * otherwise the gesture is ignored instead of firing a random direction.
 *
 * WITH TEXT IN THE BOX, THE VERTICAL AXIS IS NOT THE D-PAD'S (owner, 2026-09-11:
 * 只要在文字輸入框裡面有文字的時候, 上下滑動送出方向鍵的這一個動作就需要被取消). A person
 * dragging up or down over their own words is reading them — scrolling the box —
 * and an arrow key fired into the terminal on top of that is the fight he described.
 * So: ↑↓ swipes fire only while the box is EMPTY; → stays available either way; and a
 * gesture the browser took over (pointercancel — the scroll, once #composer is
 * `.overflowing` and touch-action is pan-y) fires nothing, whatever the finger did.
 *
 * A SWIPE TO THE LEFT SENDS NOTHING (owner, 2026-10-07: 我發現空白輸入列按下 <- 經常會造成
 * 問題 … 手勢滑動輸入方向鍵的功能需要取消向左的方向鍵, 來防止誤觸). In Claude Code a Left
 * arrow at an EMPTY prompt is not a cursor key: it "moves the conversation to the
 * background" — a new conversation file, written by another process — and nothing on
 * screen says so afterwards. One stray leftward swipe did that to an agent on
 * 2026-10-04 and its Clean View stood still for three days. A key with that
 * consequence cannot sit under a gesture a thumb makes by accident, so the swipe no
 * longer has it. The gesture is still CLAIMED (capture, preventDefault): a leftward
 * drag must not turn into caret placement or pop the keyboard either — it is simply
 * inert. Anyone who needs a real ← has the key on a hardware keyboard and the buttons
 * a prompt sheet offers.
 *
 * AND NOT AT ALL WHILE CLEAN VIEW IS OPEN (same day: 當使用者開啟 Clean View 的時候, 需要
 * 取消手勢滑動輸入方向鍵的功能). That is the caller's to say — App passes `enabled` —
 * because this hook cannot see the view; the reason lives here so it is read with the
 * rest: in Clean View the terminal is not on screen, so an arrow key goes to a prompt
 * nobody is looking at.
 */

const DPAD_TH = 18
const AXIS_RATIO = 1.3
export const ARROW = { up: '\x1b[A', down: '\x1b[B', left: '\x1b[D', right: '\x1b[C' } as const

export type Dir = keyof typeof ARROW

/** The owner's rule, in one place: text in the box takes the vertical axis away. */
const verticalYielded = (el: HTMLTextAreaElement, dx: number, dy: number) =>
  el.value.length > 0 && Math.abs(dy) >= Math.abs(dx)

export function useDpad(
  socketRef: { current: TerminalSocket | null },
  enabled: boolean,
) {
  const start = useRef<{ x: number; y: number } | null>(null)
  const moved = useRef(false)
  const [hint, setHint] = useState<Dir | null>(null)
  const hintTimer = useRef<number | null>(null)

  const flash = useCallback((d: Dir) => {
    setHint(d)
    if (hintTimer.current !== null) window.clearTimeout(hintTimer.current)
    hintTimer.current = window.setTimeout(() => setHint(null), 200)
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLTextAreaElement>) => {
      if (!enabled) return
      // Don't let the touch focus the textarea yet — that would pop (and then, when it
      // blurs, retract) the soft keyboard on a swipe.
      e.preventDefault()
      start.current = { x: e.clientX, y: e.clientY }
      moved.current = false
    },
    [enabled],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLTextAreaElement>) => {
      const s = start.current
      if (!enabled || !s) return
      const dx = e.clientX - s.x
      const dy = e.clientY - s.y
      if (!moved.current && Math.hypot(dx, dy) < DPAD_TH) return
      // A vertical drag over text is the person's, not ours: no capture, no
      // preventDefault, so the browser's scroll (pan-y on an overflowing box) is
      // not fought for — and nothing is sent at the end either (onPointerUp).
      if (verticalYielded(e.currentTarget, dx, dy)) return
      if (!moved.current) {
        moved.current = true
        e.currentTarget.setPointerCapture(e.pointerId)
      }
      e.preventDefault()
    },
    [enabled],
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLTextAreaElement>) => {
      const s = start.current
      start.current = null
      if (!enabled || !s) return

      const dx = e.clientX - s.x
      const dy = e.clientY - s.y
      if (!moved.current) {
        // Not a swipe we claimed. A tap means "I want to type" — this is the one
        // place focus may move. A yielded vertical drag over text is not a tap.
        if (Math.hypot(dx, dy) < DPAD_TH) e.currentTarget.focus()
        return
      }
      moved.current = false

      const ax = Math.abs(dx)
      const ay = Math.abs(dy)
      let dir: Dir | null = null
      if (ax > ay * AXIS_RATIO) dir = dx > 0 ? 'right' : 'left'
      else if (ay > ax * AXIS_RATIO) dir = dy > 0 ? 'down' : 'up'
      // Ambiguous diagonal: do nothing rather than guess.
      if (!dir) return
      // Left is recognised and deliberately thrown away — see the head of this file.
      if (dir === 'left') return
      // The owner's rule, checked again at the end: a swipe that started as a
      // horizontal claim and ended vertical over text sends nothing.
      if ((dir === 'up' || dir === 'down') && e.currentTarget.value.length > 0) return

      socketRef.current?.write(ARROW[dir])
      flash(dir)
    },
    [enabled, socketRef, flash],
  )

  // The browser took the gesture (a scroll, a system edge swipe): whatever the
  // finger travelled, it was not a d-pad swipe. Reset, send nothing.
  const onPointerCancel = useCallback(() => {
    start.current = null
    moved.current = false
  }, [])

  return {
    hint,
    handlers: enabled ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel } : {},
  }
}
